import React, { useState, useContext, useEffect, useRef } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { AppBar, Toolbar, Typography, MenuItem, Menu, Avatar, Box, CircularProgress, Backdrop } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { AuthContext } from './context/AuthContext';
import AllocationSetupSwitcher from './components/allocation/AllocationSetupSwitcher';
import api from './authentication/axiosInstance';
import styles from './Layout.module.css';
import { getVersionDisplay } from './utils/version';
import { 
  IconFlare, IconUser, IconLogout, IconBell, IconChevronDown, 
  IconUsers, IconSnowflake, IconChartBar, IconTractor, IconRoute,
  IconCalculator, IconMessageCircle, IconHistory, IconCloud,
  IconQuestionMark, IconSettings, IconBriefcase, IconFolder,
  IconCurrencyDollar, IconThermometer, IconCheckupList, IconMapPin,
  IconCar, IconCloudStorm, IconRoute2
} from '@tabler/icons-react';
import { motion, useAnimate, stagger } from 'framer-motion';
import { message } from 'antd';
import moment from "moment";


const theme = createTheme({
  palette: {
    primary: {
      main: '#003d2f',
    },
    secondary: {
      main: '#f5a623',
    },
  },
});

const NavLink = ({ children, ...props }) => (
  <Typography
    component={Link}
    className={styles.link}
    sx={{ 
      fontFamily: "'Plus Jakarta Sans', sans-serif",
      fontSize: '15px',
      fontWeight: 500,
      textDecoration: 'none',
      color: props.color || 'var(--off-white, #FBFBFB)'
    }}
    {...props}
  >
    {children}
  </Typography>
);

const DropdownTrigger = ({ children, isActive, onClick }) => (
  <div 
    className={`${styles.link} ${isActive ? styles.activeLink : ''}`}
    onClick={onClick}
    style={{ color: 'var(--off-white, #FBFBFB)' }}
  >
    <span className={styles.linkText}>{children}</span>
    <IconChevronDown size={16} style={{ color: 'var(--off-white, #FBFBFB)' }} />
  </div>
);

const Layout = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [expandPlan, setExpandPlan] = useState(false);
  const [expandDatabases, setExpandDatabases] = useState(false);
  const [expandExecute, setExpandExecute] = useState(false);
  const [expandIntegrations, setExpandIntegrations] = useState(false);
  const [expandDebrief, setExpandDebrief] = useState(false);
  const [user, setUser] = useState({
    firstName: '',
    lastName: '',
  });
  const { setIsAuthenticated, userRole } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [isSyncing, setIsSyncing] = useState(false);
  const [dropdownStates, setDropdownStates] = useState({
    plan: false,
    execute: false,
    debrief: false,
    databases: false,
    integrations: false
  });

  const toggleDropdown = (menu) => {
    setDropdownStates(prev => {
      const newState = Object.keys(prev).reduce((acc, key) => {
        acc[key] = key === menu ? !prev[key] : false;
        return acc;
      }, {});
      return newState;
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(`.${styles.menuContainer}`)) {
        setDropdownStates({
          plan: false,
          execute: false,
          debrief: false,
          databases: false,
          integrations: false
        });
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const fetchNotifications = async (page = 1, pageSize = 10) => {
    try {
      const response = await api.get('/notifications/notifications/', {
        params: {
          page, 
          pageSize,
        }
      });
      const newNotifications = response.data.data;
      setNotifications((prevNotifications) => (page === 1 ? newNotifications : [...prevNotifications, ...newNotifications]));
      setNotificationsCurrentPage(response.data.page);
      setNotificationsTotalPages(response.data.totalPages);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      message.error('Failed to fetch notifications');
    }
  };

  const loadMoreNotifications = async () => {
    if (notificationsCurrentPage >= notificationsTotalPages || notificationsLoadingMore) return;
    setNotificationsLoadingMore(true);
    await fetchNotifications(notificationsCurrentPage + 1);
    setNotificationsLoadingMore(false);
  };

  useEffect(() => {
    fetchNotifications(1,10);
  }, []);


  useEffect(() => {
    const fetchUser = async () => {
      try {
        const { data } = await api.get('/users/profile/');
        setUser({
          firstName: data.first_name,
          lastName: data.last_name,
        });
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    fetchUser();
  }, []);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfileClick = () => {
    navigate('/profile');
    handleMenuClose();
  };

  const handleLogoutClick = async () => {
    try {
      // Get the refresh token from localStorage
      const refreshToken = localStorage.getItem('refreshToken');
  
      // Make API call to logout and blacklist the token
      await api.post('/users/logout/', { refresh_token: refreshToken });
  
      if (!refreshToken) {
        console.error("No refresh token found");
        return;
      }

      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      
      // Reset AuthContext state
      setIsAuthenticated(false);
  
      // Redirect the user to the login page
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      handleMenuClose(); // Close the dropdown menu after logout
    }
  };

  const [scope, animate] = useAnimate();
  const sublinksRef = useRef([]);
  const allocateLinksRef = useRef([]);
  const dispatchLinksRef = useRef([]);
  const integrationsLinksRef = useRef([]);
  const debriefLinksRef = useRef([]);

  useEffect(() => {
    if (expandDatabases) {
      animate(sublinksRef.current, { opacity: 1, y: 0 }, { delay: stagger(0.1) });
    }
  }, [expandDatabases, animate]);
  useEffect(() => {
    if (expandPlan) {
      animate(allocateLinksRef.current, { opacity: 1, y: 0 }, { delay: stagger(0.1) });
    }
  }, [expandPlan, animate]);
  useEffect(() => {
    if (expandExecute) {
      animate(dispatchLinksRef.current, { opacity: 1, y: 0 }, { delay: stagger(0.1) });
    }
  }, [expandExecute, animate]);
  useEffect(() => {
    if (expandIntegrations) {
      animate(integrationsLinksRef.current, { opacity: 1, y: 0 }, { delay: stagger(0.1) });
    }
  }, [expandIntegrations, animate]);
  useEffect(() => {
    if (expandDebrief) {
      animate(debriefLinksRef.current, { opacity: 1, y: 0 }, { delay: stagger(0.1) });
    }
  }, [expandDebrief, animate]);

  const handleClick = () => {
    setExpandDatabases(!expandDatabases);
    setExpandPlan(false);
    setExpandExecute(false);
    setExpandIntegrations(false);
    setExpandDebrief(false);
  };

  const handlePlanClick = () => {
    setExpandPlan(!expandPlan);
    setExpandDatabases(false);
    setExpandExecute(false);
    setExpandIntegrations(false);
    setExpandDebrief(false);
  };

  const handleExecuteClick = () => {
    setExpandExecute(!expandExecute);
    setExpandPlan(false);
    setExpandDatabases(false);
    setExpandIntegrations(false);
    setExpandDebrief(false);
  };

  const handleIntegrationsClick = () => {
    setExpandIntegrations(!expandIntegrations);
    setExpandPlan(false);
    setExpandDatabases(false);
    setExpandExecute(false);
    setExpandDebrief(false);
  };

  const handleDebriefClick = () => {
    setExpandDebrief(!expandDebrief);
    setExpandPlan(false);
    setExpandDatabases(false);
    setExpandExecute(false);
    setExpandIntegrations(false);
  };

  useEffect(() => {
    if (location.pathname === "/") {
      setExpandDatabases(false);
      setExpandPlan(false);
      setExpandExecute(false);
      setExpandIntegrations(false);
      setExpandDebrief(false);
    }
  }, [location.pathname]);

  const syncFleetioData = async (event) => {
    event.preventDefault();
    setIsSyncing(true);
    console.log("Syncing Fleetio data...");
    try {
      const response = await api.post('/fleetio_connect/', {}, { responseType: 'blob' });
      console.log("Sync response:", response);
      
      // Create a new Blob object using the response data as a Blob
      const blob = new Blob([response.data], { type: 'application/pdf' });
      
      // Create a date string for the filename
      const date = new Date().toISOString().split('T')[0];
      
      // Create a URL for the blob and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `fleetio_sync_report_${date}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      message.success('Sync report downloaded. Review it for results.');
    } catch (error) {
      console.error("Failed to sync Fleetio data:", error);
      message.error(error.response?.data?.message || "An error occurred while syncing data");
    } finally {
      setIsSyncing(false);
      console.log("Sync process completed");
    }
  };

  const [openNotifications, setOpenNotifications] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [notificationsCurrentPage, setNotificationsCurrentPage] = useState(1);
  const [notificationsTotalPages, setNotificationsTotalPages] = useState(1);
  const [notificationsLoadingMore, setNotificationsLoadingMore] = useState(false);
  const allocationSetupRoutes = ['/allocation', '/staffAllocation', '/walkwayCrew', '/SaltingRoute'];
  const showAllocationSetupBar = userRole !== 'client' && allocationSetupRoutes.includes(location.pathname);

  const handleNotificationClick = async (id) => {
    try {
      const response = await api.post("/notifications/notifications/", {
        mark_as_read: true,
        notification_id: id,
      });
    } catch (error) {
      console.error("failed to mark as read:", error);
    } finally {
      fetchNotifications();
      message.success("Notification marked as read");
      const notification_type = notifications.filter((notification) => notification.notification_id === id)[0].type;
      if (notification_type === "Dispatch") {
        navigate("/dispatchHistory");
      } else if (notification_type === "requests") {
        navigate("/requests");
      } else if (notification_type === "Warning") {
        navigate("/messages");
      }
    }
  };
  
  

  const markAllAsRead = async () => {
    try {
      const response = await api.post("/notifications/notifications/", {
        mark_all_as_read: true,
      });
    } catch (error) {
      console.error("Failed to mark all as read:", error);
    } finally {
      fetchNotifications();
      message.success("All Notifications marked as read");
    }
  };

  const clearAllNotifications = () => {
  }
  
  return (
    <ThemeProvider theme={theme}>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        <AppBar position="static" className={styles.main} style={{ 
          backgroundImage: "linear-gradient(90deg, #D1AD53 0%, #0A3647 100%)",
          borderBottom: "var(--stroke-weight-1_25, 1.25px) solid var(--color-black-4, rgba(0, 0, 0, 0.04))"
        }}>
          <Toolbar style={{ gap: "0px", justifyContent: "space-between" }}>
            <Box className={styles.left} onClick={() => navigate('/')} style={{ flexGrow: 0 }}>
              <Box className={styles.icon} style={{ backgroundColor: location.pathname === "/" ? "white" : "#f5f5f5" }}>
                <IconFlare style={{ color: "#034832"}} size={18} strokeWidth={3}/>
              </Box>
              <Typography className={styles.title} style={{ flexGrow: 0 }}>
                Facilitate
              </Typography>
            </Box>
            {location.pathname !== "/" && userRole !== 'client' && (
              <Box className={styles.links} style={{ color: "var(--off-white, #FBFBFB)", flexGrow: 1, justifyContent: "center", gap: "86px" }}>
                <div className={styles.menuContainer}>
                  <DropdownTrigger 
                    isActive={dropdownStates.plan}
                    onClick={() => toggleDropdown('plan')}
                  >
                    Plan
                  </DropdownTrigger>
                  <div className={`${styles.dropdownMenu} ${dropdownStates.plan ? styles.dropdownMenuVisible : ''}`}>
                    <Link to="/allocation" className={styles.dropdownMenuItem}>
                      <IconTractor size={18} />
                      Loaders
                    </Link>
                    <Link to="/staffAllocation" className={styles.dropdownMenuItem}>
                      <IconUser size={18} />
                      Loaders Staff
                    </Link>
                    <Link to="/walkwayCrew" className={styles.dropdownMenuItem}>
                      <IconUsers size={18} />
                      Walkway Crews
                    </Link>
                    <Link to="/SaltingRoute" className={styles.dropdownMenuItem}>
                      <IconRoute size={18} />
                      Salting Routes
                    </Link>
                    <Link to="/projection" className={styles.dropdownMenuItem}>
                      <IconChartBar size={18} />
                      Projections
                    </Link>
                    <div className={styles.dropdownDivider} />
                    <Link to="/estimatepage" className={styles.dropdownMenuItem}>
                      <IconCalculator size={18} />
                      Estimates
                    </Link>
                    <Link to="/reports" className={styles.dropdownMenuItem}>
                      <IconUsers size={18} />
                      CRM
                    </Link>
                  </div>
                </div>

                <div className={styles.menuContainer}>
                  <DropdownTrigger 
                    isActive={dropdownStates.execute}
                    onClick={() => toggleDropdown('execute')}
                  >
                    Execute
                  </DropdownTrigger>
                  <div className={`${styles.dropdownMenu} ${dropdownStates.execute ? styles.dropdownMenuVisible : ''}`}>
                    <Link to="/walkwayCrewDispatch" className={styles.dropdownMenuItem}>
                      <IconUsers size={18} />
                      Walkway Crews
                    </Link>
                    <Link to="/SaltingRouteDispatch" className={styles.dropdownMenuItem}>
                      <IconRoute2 size={18} />
                      Salting Routes
                    </Link>
                    <Link to="/loaderDispatch" className={styles.dropdownMenuItem}>
                      <IconTractor size={18} />
                      Loaders
                    </Link>
                    <Link to="/dispatchHistory" className={styles.dropdownMenuItem}>
                      <IconHistory size={18} />
                      History
                    </Link>
                    <Link to="/messages" className={styles.dropdownMenuItem}>
                      <IconMessageCircle size={18} />
                      Messages
                    </Link>
                    <div className={styles.dropdownDivider} />
                    <Link to="/bulk-storm-strategy" className={styles.dropdownMenuItem}>
                      <IconCloud size={18} />
                      Bulk Storm Strategy
                    </Link>
                    <Link to="/storm-strategies" className={styles.dropdownMenuItem}>
                      <IconHistory size={18} />
                      Strategy History
                    </Link>
                    <Link to="/storm-strategy-surveys" className={styles.dropdownMenuItem}>
                      <IconQuestionMark size={18} />
                      Survey Responses
                    </Link>
                    <Link to="/requests" className={styles.dropdownMenuItem}>
                      <IconSettings size={18} />
                      Requests
                    </Link>
                  </div>
                </div>

                <div className={styles.menuContainer}>
                  <DropdownTrigger 
                    isActive={dropdownStates.debrief}
                    onClick={() => toggleDropdown('debrief')}
                  >
                    Debrief
                  </DropdownTrigger>
                  <div className={`${styles.dropdownMenu} ${dropdownStates.debrief ? styles.dropdownMenuVisible : ''}`}>
                    <Link to="/debriefJobs" className={styles.dropdownMenuItem}>
                      <IconBriefcase size={18} />
                      Jobs
                    </Link>
                    <Link to="/projects" className={styles.dropdownMenuItem}>
                      <IconFolder size={18} />
                      Projects
                    </Link>
                    <Link to="/exploreJobs" className={styles.dropdownMenuItem}>
                      <IconUsers size={18} />
                      Explore Jobs
                    </Link>
                    <Link to="/jobsSummary" className={styles.dropdownMenuItem}>
                      <IconCurrencyDollar size={18} />
                      Summary
                    </Link>
                    <Link to="/historical-weather" className={styles.dropdownMenuItem}>
                      <IconThermometer size={18} />
                      Weather History
                    </Link>
                    <Link to="/staff-assessments" className={styles.dropdownMenuItem}>
                      <IconCheckupList size={18} />
                      Staff Assessments
                    </Link>
                  </div>
                </div>

                <div className={styles.menuContainer}>
                  <DropdownTrigger 
                    isActive={dropdownStates.databases}
                    onClick={() => toggleDropdown('databases')}
                  >
                    Databases
                  </DropdownTrigger>
                  <div className={`${styles.dropdownMenu} ${dropdownStates.databases ? styles.dropdownMenuVisible : ''}`}>
                    <Link to="/clients" className={styles.dropdownMenuItem}>
                      <IconUsers size={18} />
                      Clients
                    </Link>
                    <Link to="/sites" className={styles.dropdownMenuItem}>
                      <IconMapPin size={18} />
                      Sites
                    </Link>
                    <Link to="/vehicles" className={styles.dropdownMenuItem}>
                      <IconCar size={18} />
                      Vehicles
                    </Link>
                    <Link to="/equipments" className={styles.dropdownMenuItem}>
                      <IconTractor size={18} />
                      Equipments
                    </Link>
                    <Link to="/staff" className={styles.dropdownMenuItem}>
                      <IconUsers size={18} />
                      Staff
                    </Link>
                    <Link to="/subtypes" className={styles.dropdownMenuItem}>
                      <IconCar size={18} />
                      Vehicle Subtypes
                    </Link>
                    <Link to="/storms" className={styles.dropdownMenuItem}>
                      <IconCloudStorm size={18} />
                      Storms
                    </Link>
                  </div>
                </div>

                <div className={styles.menuContainer}>
                  <DropdownTrigger 
                    isActive={dropdownStates.integrations}
                    onClick={() => toggleDropdown('integrations')}
                  >
                    Integrations
                  </DropdownTrigger>
                  <div className={`${styles.dropdownMenu} ${dropdownStates.integrations ? styles.dropdownMenuVisible : ''}`}>
                    <div className={`${styles.dropdownMenuItem} ${styles.imageItem}`} onClick={syncFleetioData}>
                      <img src="/Fleetio Logo.png" alt="Fleetio" />
                    </div>
                  </div>
                </div>
              </Box>
            )}
            {userRole === 'client' && (
              <Box className={styles.links} style={{ color: "var(--off-white, #FBFBFB)", flexGrow: 1, justifyContent: "center" }}>
                <NavLink to="/requests" style={{ color: "var(--off-white, #FBFBFB)" }}>Requests</NavLink>
              </Box>
            )}
            
            <Box style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              {userRole !== 'client' && (
                <Box className={styles.notificationButton} onClick={(e) => setOpenNotifications(e.currentTarget)} style={{ color: "var(--off-white, #FBFBFB)", margin: 0 }}>
                  <IconBell size={20} style={{ color: "black" }} />
                  {notifications.filter((notification) => !notification.read).length > 0 && <Box className={styles.notificationBadge} />}
                </Box>
              )}
              <Avatar className={styles.avatar} onClick={handleMenuClick} src="https://i.pinimg.com/474x/d6/cd/f2/d6cdf2a5daaf96462127cc31fb621851.jpg">
                {user?.firstName ? user.firstName.charAt(0) : ''}
              </Avatar>
            </Box>
            
            <Menu
              anchorEl={openNotifications}
              open={Boolean(openNotifications)}
              onClose={() => setOpenNotifications(null)}
              disableAutoFocusItem
              sx={{
                "& .MuiMenu-paper": {
                  boxShadow:
                    "rgba(145, 158, 171, 0.2) 0px 0px 2px 0px, rgba(145, 158, 171, 0.12) 0px 12px 24px -4px",
                  borderRadius: "3px",
                  fontFamily: "var(--font)",
                  fontWeight: "600",
                  fontSize: "16px",
                  marginTop: "5px",
                  width: "525px", // Ensure consistent width
                  marginLeft: "0px",
                  border: "0.5px solid #E4E3E3",
                },
              }}
            >
              <Box className={styles.notificationsContainerHeader}>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', width:'100%'}}>
                  <div>
                    <Typography className={styles.nchTitle}>Notifications</Typography>
                  </div>
                  <div style={{display:'flex', paddingLeft:100}}>
                    <Typography
                      className={styles.nchTextButton}
                      onClick={(e) => {
                        navigate("/notifications");
                        setOpenNotifications(null)
                      }}
                    >
                      See All Notifications
                    </Typography>
                    <Typography className={styles.nchTextButton} onClick={markAllAsRead} style={{marginLeft:10}}>
                      Mark all as read
                    </Typography>
                  </div>
                </div>
              </Box>

              <Box
                className={styles.notificationsContainer}
                sx={{
                  overflowY: "auto", // Enable vertical scrolling if content exceeds
                  overflowX: "hidden", // Disable horizontal scrolling
                  "&::-webkit-scrollbar": {
                    width: "8px", // Set scrollbar width
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "transparent", // Invisible by default
                    borderRadius: "4px",
                  },
                  "&:hover::-webkit-scrollbar-thumb": {
                    backgroundColor: "#bdbdbd", // Show when hovered
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "transparent", // Optional track background
                  },
                }}
              >
                {notifications && notifications.length > 0 ? (
                  notifications.map((notification) => (
                    <Box
                      key={notification.notification_id}
                      className={styles.notificationCard}
                      onClick={() => handleNotificationClick(notification.notification_id)}
                      style={{
                        backgroundColor:
                          notification.status === "Confirmed" ? "#d3edd5" : "#fcebeb",
                      }}
                    >
                      <Box
                        className={styles.notificationCardIndicator}
                        style={{
                          backgroundColor: notification.read ? "transparent" : undefined,
                        }}
                      />
                      <Box
                        className={styles.ncInnerCard}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          overflowWrap: "normal", // Prevents breaking inside words
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: "var(--font)",
                            fontSize: "14px",
                            width: "100%", // Ensure it respects the parent width
                            overflowWrap: "normal", // Prevents breaking inside words
                          }}
                        >
                          <span style={{ fontWeight: "600" }}>
                            {notification.staff_details.staff_name}
                          </span>{" "}
                          {notification.status === "Response" ? "Sent" 
                            : notification.status === "Confirmed"? "Confirmed"
                              : "Denied"}{" "}
                          {notification.type === "Warning" && (
                            <>
                              <span>Custom Message - </span>
                              <span style={{ fontWeight: "600" }}>
                                Storm ID: {notification.storm_details?.storm_id}{" "} <br/>
                                Type: {notification.storm_details?.storm_type} - Date:{moment(notification.storm_details?.date_of_storm).format("MMMM Do YYYY")}{" "}
                              </span>
                            </>
                          )}
                          {notification.type === "Dispatch" && (
                            <>
                              <span style={{ fontWeight: "600" }}>
                                Dispatch id: {notification.dispatch_log_details.dispatch_log_id}{" "}
                                -{" "}
                              </span>{" "}
                              (Type:{" "}
                              <br/>
                              <span style={{ fontWeight: "600" }}>
                                {notification.dispatch_log_details.dispatch_type}
                              </span>{" "}
                              - for{" "}
                              <span>
                                {moment(notification.dispatch_log_details.dispatched_for).format("MMMM Do YYYY")}{" "}
                              </span>{" "}
                              <span>
                                {moment(notification.dispatch_log_details.dispatched_for).format("h:mm a")}
                              </span>
                              )
                            </>
                          )}
                          {notification.type === "Response" && (
                              <span style={{ fontWeight: "600" }}>
                                text message: {notification.received_message}
                              </span>
                          )}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: "var(--font)",
                            fontSize: "10px",
                            color: "grey",
                            overflowWrap: "normal", // Prevents breaking inside words
                          }}
                        >
                          <span>
                            {moment(notification.dispatched_at).format("MMMM Do YYYY")} @{" "}
                          </span>
                          <span>
                            {moment(notification.dispatched_at).format("h:mm:ss a")}
                          </span>
                        </Typography>
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Typography>No notifications available</Typography>
                )}
                {notificationsCurrentPage < notificationsTotalPages && (
                  <Box
                  className={styles.loadMoreContainer}
                  onClick={loadMoreNotifications}
                  style={{
                    textAlign: 'center',
                    padding: '10px 0',
                    cursor: notificationsLoadingMore ? 'not-allowed' : 'pointer',
                    color: notificationsLoadingMore ? 'gray' : '#003d2f',
                  }}
                >
                  {notificationsLoadingMore ? 'Loading...' : 'Load More'}
                </Box>
                )}
              </Box>
            </Menu>
            
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              disableAutoFocusItem
              sx={{
                "& .MuiMenu-paper": {
                  boxShadow:
                    "rgba(145, 158, 171, 0.2) 0px 0px 2px 0px, rgba(145, 158, 171, 0.12) 0px 12px 24px -4px",
                  borderRadius: "3px",
                  fontFamily: "var(--font)",
                  fontWeight: "600",
                  fontSize: "16px",
                  marginTop: "5px",
                  marginLeft: "-16px"
                },
              }}
            >
              <MenuItem onClick={handleProfileClick} className={styles.menuItem}>
                <IconUser size={18} />
                <Typography className={styles.menuText}>
                  Profile
                </Typography>
              </MenuItem>
              <MenuItem onClick={handleLogoutClick} className={styles.menuItem}>
                <IconLogout size={18} />
                <Typography className={styles.menuText}>
                  Logout
                </Typography>
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>
        {showAllocationSetupBar && <AllocationSetupSwitcher />}
        <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <div style={{ flexGrow: 1 }}>
            <Outlet />
          </div>
          <Box
            component="footer"
            sx={{
              textAlign: 'center',
              padding: '8px 16px',
              fontSize: '11px',
              color: '#666',
              borderTop: '1px solid #e0e0e0',
              backgroundColor: '#fafafa',
            }}
          >
            {getVersionDisplay()}
          </Box>
        </div>
      </div>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isSyncing}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <CircularProgress color="inherit" size={60} />
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6">Syncing Fleetio Data</Typography>
          </Box>
          <Box sx={{ mt: 1 }}>
            <Typography variant="body1">Please wait while we update your information...</Typography>
          </Box>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            {[0, 1, 2].map((index) => (
              <motion.div
                key={index}
                style={{
                  width: 20,
                  height: 20,
                  backgroundColor: 'white',
                  borderRadius: '50%',
                  margin: '0 5px',
                }}
                animate={{
                  y: [0, -20, 0],
                  opacity: [1, 0.5, 1],
                }}
                transition={{
                  duration: 1,
                  repeat: Infinity,
                  delay: index * 0.2,
                }}
              />
            ))}
          </Box>
        </Box>
      </Backdrop>
    </ThemeProvider>
  );
};

export default Layout;
